import {
  Box,
  CSSObject,
  HStack,
  Radio,
  RadioGroup as ChakraRadioGroup,
  RadioGroupProps as ChakraRadioGroupProps,
  useMultiStyleConfig,
  useRadio,
  useRadioGroup,
  UseRadioProps,
} from '@chakra-ui/react';

export interface RadioGroupProps
  extends Omit<ChakraRadioGroupProps, 'children'> {
  optionsType?: 'default' | 'button';
  options?:
    | string[]
    | number[]
    | { label: React.ReactNode; value: string | number }[];
  defaultValue?: string | number;
  variant?: 'outline' | 'solid';
  radioProps?: CSSObject;
  radioButtonProps?: CSSObject;
  applyBorderRadiusForStartEndButtons?: boolean;
  borderRadiusForStartEndButtons?: string | number;
}

export interface RadioButtonProps extends UseRadioProps {
  sx?: CSSObject;
  variant?: 'outline' | 'solid';
  children?: React.ReactNode;
}

export const RadioGroup = (props: RadioGroupProps) => {
  const {
    options = [],
    optionsType = 'default',
    variant,
    radioProps,
    radioButtonProps,
    applyBorderRadiusForStartEndButtons,
    borderRadiusForStartEndButtons,
    ...rest
  } = props;
  const { getRootProps, getRadioProps, htmlProps } = useRadioGroup({ ...rest });
  const group = getRootProps();

  const styles = useMultiStyleConfig('RadioGroup', props);

  return optionsType === 'default' ? (
    <ChakraRadioGroup sx={{ ...styles.RadioGroup, ...rest }}>
      {options.map((option, index) => {
        const isOptionObject = !(
          typeof option === 'string' || typeof option === 'number'
        );
        return (
          <Radio
            key={index}
            value={isOptionObject ? option.value : option}
            sx={{ ...styles.Radio, ...radioProps }}
          >
            {isOptionObject ? option.label : option}
          </Radio>
        );
      })}
    </ChakraRadioGroup>
  ) : (
    <HStack sx={{ ...styles.RadioStack, ...group, ...htmlProps }}>
      {options.map((option, index) => {
        const isOptionObject = !(
          typeof option === 'string' || typeof option === 'number'
        );
        const radio = getRadioProps({
          value: isOptionObject ? option.value : option,
        });
        const getStartEndBorderProps = () => {
          return applyBorderRadiusForStartEndButtons
            ? {
                borderLeftRadius:
                  index === 0 ? borderRadiusForStartEndButtons : 0,
                borderRightRadius:
                  index === options.length - 1
                    ? borderRadiusForStartEndButtons
                    : 0,
              }
            : {};
        };
        return (
          <RadioButton
            key={index}
            value={isOptionObject ? option.value : option}
            variant={variant}
            sx={{
              ...styles.RadioButton,
              ...radioButtonProps,
              ...getStartEndBorderProps(),
            }}
            {...radio}
          >
            {isOptionObject ? option.label : option}
          </RadioButton>
        );
      })}
    </HStack>
  );
};

export const RadioButton = (props: RadioButtonProps) => {
  const { getInputProps, getCheckboxProps } = useRadio(props);
  const input = getInputProps();
  const checkbox = getCheckboxProps();

  const styles = useMultiStyleConfig('RadioGroup', props);

  return (
    <Box as='label' sx={{ ...styles.ButtonContainer }}>
      <input {...input} spellCheck={false} />
      <Box {...checkbox} sx={{ ...styles.RadioButton }} {...props}>
        {props.children}
      </Box>
    </Box>
  );
};
